import * as React from "react"
import { Link } from "react-router-dom"
import { useColorMode } from "@chakra-ui/react"
import { useTheme } from "next-themes"

import { AuthPageShell } from "@/components/auth-page-shell"
import {
  AUTH_MUTED_TEXT_CLASS,
  authSplitCardClass,
  authSplitCardShellClass,
} from "@/components/auth-split-card-layout"
import { Card, CardContent } from "@/components/ui/card"
import { FrozenLoadingChartBackdrop } from "@/components/ui/frozen-loading-chart-backdrop"
import { SIDEBAR_BRAND_LOGO_EXPANDED_SRC } from "@/constants/sidebarBrandLogo"
import { cn } from "@/lib/utils"
import { DEMO_ENTRY_ANALYSIS_BASE_PATH } from "@/utils/demoEntryAnalysisSession"

const AUTH_FONT = "font-[family-name:var(--font-primary)]"
const AUTH_TROPICAL = "text-[color:var(--brand-tropical-surf)]"

/** Page title under the funnel stepper — size/color forced in globals.css (Chakra resets h1). */
const WIZARD_TITLE_CLASS = cn(AUTH_FONT, "font-normal")

/** Match StrategyPineDetailDialog description. */
const WIZARD_DESC_CLASS = cn(AUTH_FONT, "font-normal text-gray-500 dark:text-white")

export const DEMO_FUNNEL_STEP_COUNT = 4

const STEP_LABELS = ["Broker", "Upload", "Instrument", "Report"] as const

/** Same Tropical Surf → Neon Aqua progression as GenerateReportWizardStepper pills. */
const GRADIENT_START = { r: 0x38, g: 0xbb, b: 0xd1 }
const GRADIENT_END = { r: 0x3e, g: 0xea, b: 0xec }

function pillFillColor(index: number, stepCount: number): string {
  const t = stepCount <= 1 ? 0 : index / (stepCount - 1)
  const r = Math.round(GRADIENT_START.r + (GRADIENT_END.r - GRADIENT_START.r) * t)
  const g = Math.round(GRADIENT_START.g + (GRADIENT_END.g - GRADIENT_START.g) * t)
  const b = Math.round(GRADIENT_START.b + (GRADIENT_END.b - GRADIENT_START.b) * t)
  return `rgb(${r}, ${g}, ${b})`
}

/** Demo stays light-only; keep Chakra `_dark` chrome in sync. */
function DemoForceLightTheme(): null {
  const { setTheme } = useTheme()
  const { colorMode, setColorMode } = useColorMode()

  React.useEffect(() => {
    setTheme("light")
    if (colorMode !== "light") {
      setColorMode("light")
    }
  }, [colorMode, setColorMode, setTheme])

  return null
}

function DemoBrandStrip({ compact = false }: { compact?: boolean }): React.ReactElement {
  return (
    <div
      data-demo-brand-strip=""
      className={cn(
        "relative flex w-full shrink-0 items-center justify-between gap-3 overflow-hidden md:gap-4",
        compact ? "px-5 py-5 md:px-6 md:py-5" : "px-6 py-6 md:px-8 md:py-7"
      )}
      style={{
        backgroundImage: "var(--gradient-primary)",
      }}
    >
      <Link
        to={DEMO_ENTRY_ANALYSIS_BASE_PATH}
        className="inline-flex min-w-0 flex-1 items-center justify-between gap-3 text-white hover:opacity-95 md:gap-4"
        aria-label="Tessera Entry Analysis demo"
      >
        <div className="flex min-w-0 flex-col justify-center gap-2">
          <span
            className={cn(
              AUTH_FONT,
              "truncate text-xl font-normal leading-tight tracking-normal text-white md:text-2xl"
            )}
          >
            Entry Analysis demo
          </span>
          {!compact ? (
            <span
              className={cn(
                AUTH_FONT,
                "max-w-xl text-base font-normal leading-snug text-white/90 md:text-lg"
              )}
            >
              Find the entry habits quietly draining your edge.
            </span>
          ) : null}
        </div>
        <img
          src={SIDEBAR_BRAND_LOGO_EXPANDED_SRC}
          alt=""
          width={220}
          height={48}
          decoding="async"
          className="block h-12 w-auto max-w-[13.75rem] shrink-0 self-center object-contain object-right brightness-0 invert"
          style={{ width: "auto", height: 48, maxWidth: 220 }}
        />
      </Link>
    </div>
  )
}

function DemoFunnelProgress({ activeStepIndex }: { activeStepIndex: number }) {
  return (
    <div className="w-full min-w-0" data-demo-funnel-progress="">
      <div
        className="flex w-full items-center gap-1.5"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={DEMO_FUNNEL_STEP_COUNT}
        aria-valuenow={activeStepIndex + 1}
        aria-label={`Step ${activeStepIndex + 1} of ${DEMO_FUNNEL_STEP_COUNT}`}
      >
        {STEP_LABELS.map((label, index) => {
          const filled = index <= activeStepIndex
          return (
            <span
              key={label}
              className={cn(
                "block h-1 min-w-0 flex-1 rounded-full transition-[background-color,opacity] duration-200",
                !filled && "bg-muted dark:bg-input"
              )}
              style={filled ? { backgroundColor: pillFillColor(index, DEMO_FUNNEL_STEP_COUNT) } : undefined}
            />
          )
        })}
      </div>
      <div className="mt-2 grid w-full grid-cols-4 gap-1.5">
        {STEP_LABELS.map((label, index) => {
          const isActive = index === activeStepIndex
          const isDone = index < activeStepIndex
          return (
            <span
              key={label}
              className={cn(
                AUTH_FONT,
                "text-center text-[0.6875rem] font-normal leading-tight tracking-wide sm:text-xs",
                isActive && AUTH_TROPICAL,
                !isActive && !isDone && "text-gray-500 dark:text-white/55",
                isDone && "text-gray-500 dark:text-white/80"
              )}
            >
              {label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

/** Frozen candle-tile backdrop — same treatment as marketing Strategies hero chartBackdrop. */
function DemoCandlePageBackdrop(): React.ReactElement {
  return (
    <div
      data-demo-candle-backdrop=""
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-background"
      aria-hidden
    >
      <div className="absolute inset-0 opacity-40">
        <FrozenLoadingChartBackdrop className="!bg-transparent" />
      </div>
    </div>
  )
}

/** Dialog width — grows on larger breakpoints (card + AuthPageShell content). */
const DEMO_DIALOG_MAX_WIDTH_CLASS =
  "max-w-3xl md:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl"

/** Narrow dialogs for short messages — width follows content, capped for the brand strip. */
const DEMO_DIALOG_COMPACT_MAX_WIDTH_CLASS = "max-w-lg"

export type DemoEntryAnalysisShellLayout = "default" | "compact"

const DEMO_DIALOG_LAYOUT_CLASS: Record<DemoEntryAnalysisShellLayout, string> = {
  default: DEMO_DIALOG_MAX_WIDTH_CLASS,
  compact: DEMO_DIALOG_COMPACT_MAX_WIDTH_CLASS,
}

export function DemoEntryAnalysisShell(props: {
  children?: React.ReactNode
  title?: string
  subtitle?: string
  /** Optional meta row under the title (badges / icon chips) — same chrome as report detail. */
  titleMeta?: React.ReactNode
  footer?: React.ReactNode
  /** Zero-based funnel step for the gradient stepper (omit to hide). */
  activeStepIndex?: number
  /** `compact` sizes the card to short content; `default` is the full wizard / results width. */
  layout?: DemoEntryAnalysisShellLayout
}): React.ReactElement {
  const {
    children,
    title,
    subtitle,
    titleMeta,
    footer,
    activeStepIndex,
    layout = "default",
  } = props
  const showStepper = typeof activeStepIndex === "number"
  const isCompact = layout === "compact"
  const dialogMaxWidthClass = DEMO_DIALOG_LAYOUT_CLASS[layout]

  return (
    <>
      <DemoCandlePageBackdrop />
      <AuthPageShell
        className="relative z-[1] !bg-transparent md:p-8 dark:!bg-transparent"
        contentClassName={dialogMaxWidthClass}
      >
        <DemoForceLightTheme />
        {/*
          data-connect-broker-wizard unlocks the same card/import-method CSS
          as Broker Management (borders, hover, selected, cursor).
        */}
        <div
          className={cn(authSplitCardShellClass(), "mx-auto w-full", dialogMaxWidthClass)}
          data-demo-entry-analysis=""
          data-demo-entry-analysis-layout={layout}
          data-connect-broker-wizard=""
        >
          <Card
            className={cn(
              authSplitCardClass,
              "flex flex-col dark:!bg-sidebar dark:!text-sidebar-foreground",
              isCompact ? "min-h-0" : "min-h-[min(72vh,36rem)]"
            )}
            data-font="primary"
            data-demo-entry-analysis-dialog=""
          >
            <CardContent className="flex w-full min-w-0 flex-1 flex-col p-0">
              <DemoBrandStrip compact={isCompact} />

              <div
                className={cn(
                  "flex w-full min-w-0 flex-col gap-6 bg-card p-6 text-card-foreground dark:bg-sidebar dark:text-sidebar-foreground md:p-8",
                  isCompact ? "flex-none" : "flex-1"
                )}
              >
                {showStepper ? <DemoFunnelProgress activeStepIndex={activeStepIndex} /> : null}

                {title || subtitle || titleMeta ? (
                  <div className="flex flex-col gap-1.5">
                    {title ? (
                      <h1 data-demo-wizard-title="" className={WIZARD_TITLE_CLASS}>
                        {title}
                      </h1>
                    ) : null}
                    {titleMeta ? <div className="min-w-0 w-full">{titleMeta}</div> : null}
                    {!titleMeta && subtitle ? (
                      <p className={cn(WIZARD_DESC_CLASS, "text-sm")}>{subtitle}</p>
                    ) : null}
                  </div>
                ) : null}

                <div
                  className={cn(
                    "flex w-full min-w-0 flex-col gap-6",
                    isCompact ? "flex-none" : "flex-1"
                  )}
                >
                  {children}
                </div>
              </div>

              {footer ? (
                <div
                  className="mt-auto shrink-0 rounded-b-[6px] border-t border-border bg-muted/50 px-6 py-4 text-card-foreground dark:text-sidebar-foreground md:px-8"
                  data-demo-entry-analysis-footer=""
                >
                  {footer}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </AuthPageShell>
    </>
  )
}

/** Vertically centered hero column — matches demo generating progress layout. */
export const DEMO_CENTERED_PANEL_CLASS =
  "flex min-h-[min(40vh,20rem)] w-full flex-1 flex-col items-center justify-center gap-6 px-5 text-center sm:min-h-[min(48vh,24rem)] sm:gap-7 sm:px-4"

export function DemoCenteredPanel({
  className,
  children,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      data-demo-centered-panel=""
      className={cn(DEMO_CENTERED_PANEL_CLASS, className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function DemoLoopingEllipsis(): React.ReactElement {
  const frames = ["", ".", "..", "..."] as const
  const [frame, setFrame] = React.useState(0)

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setFrame((current) => (current + 1) % frames.length)
    }, 450)
    return () => window.clearInterval(id)
  }, [frames.length])

  return (
    <span className="inline-block w-[1.125em] text-left" aria-hidden>
      {frames[frame]}
    </span>
  )
}

/** Big centered loading title — same scale as generating progress. */
export function DemoLoadingBlock({
  label = "Loading",
  centered: _centered = true,
}: {
  label?: string
  /** @deprecated Always centered; kept for call-site compatibility. */
  centered?: boolean
}): React.ReactElement {
  void _centered
  const title = label.replace(/\.{3}|…$/u, "").trim()

  return (
    <DemoCenteredPanel data-demo-loading-panel="">
      <h1
        data-demo-wizard-title=""
        className={cn(AUTH_FONT, "w-full text-center font-normal")}
        aria-live="polite"
      >
        <span className="inline-flex items-baseline justify-center">
          <span>{title}</span>
          <DemoLoopingEllipsis />
        </span>
      </h1>
    </DemoCenteredPanel>
  )
}

/** Big centered title + body + optional actions (errors, limits, etc.). */
export function DemoCenteredMessage(props: {
  title: string
  description?: string
  action?: React.ReactNode
}): React.ReactElement {
  const { title, description, action } = props

  return (
    <DemoCenteredPanel data-demo-centered-message="">
      <div className="flex w-full max-w-lg flex-col items-center gap-4 sm:gap-5">
        <h1 data-demo-wizard-title="" className={cn(AUTH_FONT, "w-full text-center font-normal")}>
          {title}
        </h1>
        {description ? (
          <p className={cn(WIZARD_DESC_CLASS, "max-w-md text-pretty text-sm sm:text-base")}>
            {description}
          </p>
        ) : null}
        {action ? <div className="mt-1 flex w-full flex-col items-center gap-4">{action}</div> : null}
      </div>
    </DemoCenteredPanel>
  )
}

export { AUTH_FONT, AUTH_TROPICAL, AUTH_MUTED_TEXT_CLASS, WIZARD_TITLE_CLASS, WIZARD_DESC_CLASS }
